import { NgClass } from "@angular/common";
import { Component, inject, Inject, OnInit, signal } from "@angular/core";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { MatIcon } from "@angular/material/icon";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { formatDisplayCurrencyAmount } from "@core/currency-display.util";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, of, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { JackpotTriggersService } from "src/app/services/jackpot-triggers.service";
import { PaymentService } from "src/app/services/payment.service";
import { AccountRepositoryService } from "../../account/account-repository.service";
import { SyndicateRepositoryService } from "../../syndicate/syndicate-repository.service";
import { ClarityService } from "src/app/services/clarity.service";
@Component({
  selector: "app-store-jackpot-bottomsheet",
  templateUrl: "./store-jackpot-bottomsheet.component.html",
  styleUrls: ["./store-jackpot-bottomsheet.component.scss"],
  standalone: true,
  imports: [
    NgClass,
    MatIcon,
    TranslatePipe,
  ],
})
export class StoreJackpotBottomsheetComponent implements OnInit {
  jackpotName: any;
  languageCode: any;
  jackpotTriggerTranslatedData: any = {};
  userTokenExists: any;
  jackpotTriggerObj: any;
  selectedLanguage: string = "en";
  selectedLanguageSignal = signal<string>("en");
  triggerActiveCount: any;
  triggerActiveCountSignal = signal<any>([]);
  preferredCurrency: string = CommonVariable.preferredCurrency;
  preferredCurrencySignal = signal<string>(CommonVariable.preferredCurrency);
  currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  currencySymbolSignal = signal<string>(CommonVariable.defaultCurrencySymbol);
  response = new BehaviorSubject<Data>(null);
  private clarityService = inject(ClarityService);
  translatedToastMsg: any;
  promoName: string;
  isJptActived = signal<boolean>(false);
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private bottomSheetRef: MatBottomSheetRef<StoreJackpotBottomsheetComponent>,
    private router: Router,
    private authService: AuthService,
    private jackpotTriggerService: JackpotTriggersService,
    private syndicateService: SyndicateRepositoryService,
    private paymentService: PaymentService,
    public translate: TranslateService,
    public GTMservice: GTMSegmentService,
    public accountService: AccountRepositoryService,
  ) {
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.currencySymbol = CommonVariable.defaultCurrencySymbol;
    this.isJptActived.set(this.data?.isJptActived);
    this.authService.selectedlanguage$.subscribe({
      next: (l) => {
        this.selectedLanguageSignal.set(l ? l : "en");
        this.selectedLanguage = this.selectedLanguageSignal();
        translate.use(l ? l : "en");
      },
      error: (err) => {
        this.authService.errorHandlerLogs("An error occurred:", err);
      },
    });

    this.authService.userDetails$.subscribe({
      next: (user) => {
        this.preferredCurrencySignal.set(user?.preferred_currency ?? CommonVariable.preferredCurrency);
        this.preferredCurrency = this.preferredCurrencySignal();
        this.authService.getCurrencyList$.subscribe({
          next: (data: Data) => {
            const CurrencyDetails = data?.details?.find((x) => x?.currency_code === this.preferredCurrencySignal());
            this.currencySymbolSignal.set(CurrencyDetails?.currency_symbol ?? CommonVariable.defaultCurrencySymbol);
            this.currencySymbol = this.currencySymbolSignal();
          },
          error: (err) => {
            this.authService.errorHandlerLogs("An error occurred while fetching currency list:", err);
          },
        });
      },
      error: (err) => {
        this.authService.errorHandlerLogs("An error occurred while fetching user details:", err);
      },
    });

    this.authService.getConstantsMsg$.subscribe((msg: any) => {
      this.translatedToastMsg = msg?.static_messages_pwa;
    });
    this.promoName = localStorage.getItem("engagement_promo");
  }

  ngOnInit() {
    this.loadJackpotTriggerData();
    this.callPromoCF("seen");
  }

  formatCurrencyDisplay(amount: number | string | null | undefined): string {
    if (amount === null || amount === undefined || amount === "") {
      return "";
    }
    return formatDisplayCurrencyAmount(amount);
  }

  async loadJackpotTriggerData() {
    try {
      const {
        jackpot_name,
        lines,
        jackpot,
        price,
        offer_link_id,
        offer_id,
        free_jackpot = false,
        alternate_free_jackpot = false,
        free_offer_id = 0,
        alias = "",
        trigger_active_count = 0,
      } = this.data || {};

      this.jackpotName = jackpot_name;
      this.jackpotTriggerObj = {
        lines,
        jackpot,
        price,
        offer_link_id,
        offer_id,
        free_jackpot,
        alternate_free_jackpot,
        free_offer_id,
        alias,
      };

      this.triggerActiveCountSignal.set(trigger_active_count);
      this.triggerActiveCount = this.triggerActiveCountSignal();

      // Subscribe to user details
      this.authService.userDetails$
        .pipe(
          switchMap((user) => {
            const languageCode = user?.language_code || "en";
            return this.jackpotTriggerService?.getTranslatedData(languageCode) || of({});
          })
        )
        .subscribe(
          (translatedData) => {
            this.jackpotTriggerTranslatedData = translatedData?.["jackpot-trigger"] || {};
          },
          (err) => {
            this.authService.errorHandlerLogs("Failed to get translated data:", err);
          }
        );
    } catch (err) {
      this.authService.errorHandlerLogs("Error loading jackpot trigger data:", err);
    }
  }

  openPaymentPage() {
    this.callPromoCF("click");
    this.isJptActived.set(true);
    this.GTMservice.HotJarEvent("user_activate_trigger");
    this.accountService.syndicatePromotion$.pipe(take(1)).subscribe(
      (res) => {
        if (res) {
          this.jackpotTriggerObj["call_from"] = "jackpot_trigger_promotion"
        }
      }
    )
    this.authService.userDetails$.pipe(take(1)).subscribe(
      (user: Data) => {
        this.userTokenExists = user?.token_exists;
        if (this.userTokenExists || this.triggerActiveCountSignal() > 0) {
          this.syndicateService
            .activateTrigger(this.jackpotTriggerObj)
            .pipe(
              catchError((error) => {
                // RB #24242 fix
                return throwError(error);
              })
            )
            .subscribe(
              (res: any) => {
                const isSuccess = res?.status === "success";
                this.isJptActived.set(false);
                if (isSuccess) {
                  this.callPromoCF("conversion");
                  this.clarityService.event("jackpot_trigger_activated");
                  this.clarityService.setTag("product_type", "jackpot_trigger");
                  this.jackpotTriggerService.setXpAnimationStop(isSuccess);
                }
                this.jackpotTriggerService.setTriggerActivationStatus(isSuccess);
                this.response.next(res); // Notify subscribers of the response
                if (res?.status === "rejected") {
                  this.authService
                    .getConstantsMsg(res?.error_code)
                    .then((translatedMsg) => {
                      const defaultMsg = {
                        message: {
                          line_1: "Something went wrong!",
                          line_2: "",
                        },
                        style: {
                          icon: "tick circle error.svg",
                          type: "Error",
                        },
                      };
                      this.authService.showToastMessage(translatedMsg, defaultMsg);
                    })
                    .catch((error) => {
                      return error;
                    });
                  return;
                }
                if (res?.status === "fail") {
                  /* this.snackBar.openFromComponent(MyToastbarComponent, {
                duration: 5000,
                horizontalPosition: "center",
                verticalPosition: "top",
                panelClass: "toast-for-syndicate",
                data: { message: res.message ? res.message : "Process failed, Please try again", state: "Stop" },
              }); */
                  /* const msg = this.translatedToastMsg?.["1071"]?.[this.selectedLanguage];
              this.authService.showToast(res.message ? res.message : msg ? msg : "Process failed, Please try again", "Stop", ""); */
                  this.authService
                    .getConstantsMsg("1071")
                    .then((translatedMsg) => {
                      const defaultMsg = {
                        message: {
                          line_1: "Process failed, Please try again",
                          line_2: "",
                        },
                        style: {
                          icon: "tick circle error.svg",
                          type: "Error",
                        },
                      };
                      this.authService.showToastMessage(translatedMsg, defaultMsg);
                    })
                    .catch((error) => {
                      return error;
                    });
                } else {
                  try {
                    this.authService
                      .getConstantsMsg("1072")
                      .then((translatedMsg) => {
                        const defaultMsg = {
                          message: {
                            line_1: `${this.jackpotName} Jackpot Trigger activated`,
                            line_2: "",
                          },
                          style: {
                            icon: "tick circle success.png",
                            type: "Success",
                          },
                        };

                        translatedMsg.message.line_1 = `${this.jackpotName} ${translatedMsg?.message?.line_1}`;
                        this.authService.showToastMessage(translatedMsg, defaultMsg);
                      })
                      .catch((error) => {
                        return error;
                      });
                  } catch (e) { }
                }
              },
              (err) => {
                return err;
              }
            );
        } else if (!this.userTokenExists) {
          localStorage.setItem("JackpotTriggerData", JSON.stringify(this.data));
          this.router.navigate(["/pre-auth"], { queryParams: { jackpotTrigger: "requested" } });
          this.paymentService.triggerActivatingData$.next(this.data);
          this.bottomSheetRef.dismiss();
        } else {
          /* this.snackBar.openFromComponent(MyToastbarComponent, {
            duration: 5000,
            horizontalPosition: "center",
            verticalPosition: "top",
            panelClass: "toast-for-syndicate",
            data: { message: "Activating Jackpot Trigger failed", state: "Stop" },
          }); */
          /* const msg = this.translatedToastMsg?.["1074"]?.[this.selectedLanguage];
          this.authService.showToast(msg ? msg : "Activating Jackpot Trigger failed", "Stop", ""); */
          this.authService
            .getConstantsMsg("1074")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Activating Jackpot Trigger failed",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((error) => {
              return error;
            });
        }
      },
      (err) => {
        /*  const msg = this.translatedToastMsg?.["1013"]?.[this.selectedLanguage];
         this.authService.showToast(msg ? msg : "Something went wrong (user)", "Error", ""); */
        this.authService
          .getConstantsMsg("1013")
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Something went wrong (user)",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          })
          .catch((err) => {
            return err;
          });
      }
    );
    this.back()
  }

  back() {
    this.response
      .asObservable()
      .pipe(
        filter((res) => !!res), // Only proceed if there's a truthy value
        take(1) // Complete the observable after the first valid emission
      )
      .subscribe({
        next: () => {
          this.bottomSheetRef.dismiss(this.response.getValue());
        },
        error: (err) => {
          return err;
        }
      });
  }

  callPromoCF(action:string){
    if(this.promoName != "jackpot_trigger_offer") return;
    this.authService.displayPromo(action, this.promoName);
  }
}
